import React, { useEffect, useMemo, useState, Children, Component } from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Zap, Server, RefreshCw, Layers, ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { useAppStore } from '../../../../hooks/useAppStore';
import { isStrictMode } from '../../../../utils/strictMode';
import { runPipelineBatch } from '../../../../governance/pipeline/orchestrator';
import type { PipelineBatchResult } from '../../../../governance/pipeline/issueContract';
import { ALL_QUESTIONS_CORE, QUESTIONS_BY_FAMILY } from '../../../../data/questionLoader';
import { Question } from '../../../../types';
import { computeLiveSystemStats } from '../../../../governance/systemStats';
import { TAXONOMY } from '../../../../config/taxonomyRegistry';
import { VISUAL_REGISTRY } from '../../../../visuals/registry/visualRegistry';
const PIPELINE_SAMPLE_SIZE = 50;
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
const RingChart = ({
  score,
  label,
  subtitle
}: {
  score: number;
  label: string;
  subtitle: string;
}) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - score / 100 * circumference;
  const color = score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';
  return <div className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5 flex items-center gap-6" data-id="element-387">
      <div className="relative flex items-center justify-center flex-shrink-0" data-id="element-388">
        <svg className="w-24 h-24 transform -rotate-90" data-id="element-389">
          <circle cx="48" cy="48" r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-100" data-id="element-390" />
          <motion.circle cx="48" cy="48" r={radius} stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={circumference} initial={{
          strokeDashoffset: circumference
        }} animate={{
          strokeDashoffset
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} className={`${color}`} strokeLinecap="round" data-id="element-391" />
        </svg>
        <div className="absolute flex flex-col items-center justify-center" data-id="element-392">
          <span className="text-xl font-bold text-gray-900" data-id="element-393">{score}%</span>
        </div>
      </div>
      <div data-id="element-394">
        <h3 className="text-sm font-bold text-gray-900 mb-1" data-id="element-395">{label}</h3>
        <p className="text-xs text-gray-500 mb-2" data-id="element-396">{subtitle}</p>
        <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${score >= 80 ? 'bg-emerald-50 text-emerald-700' : score >= 60 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`} data-id="element-397">
          <ShieldCheck className="w-3.5 h-3.5" data-id="element-398" />
          {score >= 80 ? 'Gezond' : score >= 60 ? 'Aandacht nodig' : 'Kritiek'}
        </div>
      </div>
    </div>;
};
export function AdminDashboardHome() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const [pipelineResult, setPipelineResult] = useState<PipelineBatchResult | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [strictModeOn] = useState(() => isStrictMode());
  useEffect(() => {
    setPipelineLoading(true);
    const timer = setTimeout(() => {
      try {
        const sample = ALL_QUESTIONS_CORE.slice(0, PIPELINE_SAMPLE_SIZE) as unknown as Question[];
        const result = runPipelineBatch(sample);
        setPipelineResult(result);
      } catch {
        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const handleRefreshPipeline = () => {
    setPipelineLoading(true);
    setPipelineResult(null);
    setTimeout(() => {
      try {
        const sample = ALL_QUESTIONS_CORE.slice(0, PIPELINE_SAMPLE_SIZE) as unknown as Question[];
        const result = runPipelineBatch(sample);
        setPipelineResult(result);
      } catch {
        // silent
      } finally {
        setPipelineLoading(false);
      }
    }, 50);
  };
  const stats = useMemo(() => {
    const systemStats = computeLiveSystemStats();
    // accounts is AccountsState (object), not an array — extract array from accountsById
    const allAccounts = accounts?.accountsById ? Object.values(accounts.accountsById) : [];
    const learners = allAccounts.filter(a => a.role === 'learner');
    const teachers = allAccounts.filter(a => a.role === 'teacher');
    const parents = allAccounts.filter(a => a.role === 'parent');
    const schools = new Set(allAccounts.map(a => (a as any).schoolName).filter(Boolean));
    const now = Date.now();
    const todayThreshold = now - 24 * 60 * 60 * 1000;
    const weekThreshold = now - 7 * 24 * 60 * 60 * 1000;
    // Active learners based on lastActive on their account records
    const activeToday = learners.filter(l => l.lastActive && l.lastActive > todayThreshold).length;
    const activeThisWeek = learners.filter(l => l.lastActive && l.lastActive > weekThreshold).length;
    // Plan distribution
    const freeLearners = learners.filter(l => !(l as any).isPlusUser).length;
    const plusLearners = learners.filter(l => (l as any).isPlusUser).length;
    const total = learners.length || 1;
    const planDistribution = [{
      label: 'Gratis',
      count: freeLearners,
      pct: Math.round(freeLearners / total * 100),
      color: 'bg-gray-400'
    }, {
      label: 'Plus',
      count: plusLearners,
      pct: Math.round(plusLearners / total * 100),
      color: 'bg-blue-500'
    }];
    // Top learners by XP
    const topLearners = learners.map(l => {
      const data = getLearnerData(l.id);
      return {
        name: l.displayName,
        xp: data?.stats?.xp ?? 0
      };
    }).sort((a, b) => b.xp - a.xp).slice(0, 3);
    return {
      totalLearners: learners.length,
      totalTeachers: teachers.length,
      totalParents: parents.length,
      totalSchools: schools.size,
      totalQuestions: systemStats.totalQuestions || ALL_QUESTIONS_CORE.length,
      activeToday,
      activeThisWeek,
      planDistribution,
      topLearners
    };
  }, [accounts, getLearnerData]);
  const passCount = pipelineResult?.verdicts.PASS ?? null;
  const warnCount = pipelineResult?.verdicts.REVIEW_NEEDED ?? null;
  const failCount = pipelineResult?.verdicts.FAIL ?? null;
  const avgScore = pipelineResult?.avgScore ?? null;
  const totalValidated = passCount !== null ? passCount + (warnCount ?? 0) + (failCount ?? 0) : 0;
  const qualityScore = avgScore !== null ? `${avgScore}%` : '—';
  const systemHealth = useMemo(() => {
    let storageUsed = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          storageUsed += localStorage.getItem(key)?.length || 0;
        }
      }
    } catch (e) {
      // Ignore
    }
    const storageMb = (storageUsed / 1024 / 1024).toFixed(2);
    const storagePct = Math.min(100, Math.round(parseFloat(storageMb) / 5 * 100));
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch (e) {
      // Ignore
    }
    return {
      storageMb,
      storagePct,
      responseTime: Math.max(10, responseTime)
    };
  }, []);
  const contentStats = useMemo(() => {
    const totalFamilies = QUESTIONS_BY_FAMILY.size;
    const taxonomyFamilies = TAXONOMY.length;
    const coveragePct = taxonomyFamilies > 0 ? Math.round(totalFamilies / taxonomyFamilies * 100) : 0;
    const typeDistribution: Record<string, number> = {};
    let totalQuestions = 0;
    ALL_QUESTIONS_CORE.forEach(q => {
      typeDistribution[q.questionType] = (typeDistribution[q.questionType] || 0) + 1;
      totalQuestions++;
    });
    const registeredVisuals = Object.keys(VISUAL_REGISTRY).length;
    const usedVisuals = new Set<string>();
    ALL_QUESTIONS_CORE.forEach(q => {
      const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
      aids.forEach(aid => {
        if (aid.type) usedVisuals.add(aid.type);
      });
    });
    return {
      totalFamilies,
      taxonomyFamilies,
      coveragePct,
      typeDistribution,
      totalQuestions,
      registeredVisuals,
      usedVisualsCount: usedVisuals.size,
      visualCoveragePct: registeredVisuals > 0 ? Math.round(usedVisuals.size / registeredVisuals * 100) : 0
    };
  }, []);
  // Generate real activity based on accounts
  const RECENT_ACTIVITY = useMemo(() => {
    const activities = [];
    if (stats.totalLearners > 0) {
      activities.push({
        time: 'Zojuist',
        message: `${stats.totalLearners} leerlingen actief in het systeem`,
        type: 'info' as const
      });
    }
    if (pipelineResult) {
      activities.push({
        time: 'Recent',
        message: `Validatie: ${passCount} geslaagd, ${failCount} gefaald`,
        type: (failCount ?? 0) > 0 ? 'warning' as const : 'success' as const
      });
    }
    if (stats.totalSchools > 0) {
      activities.push({
        time: 'Vandaag',
        message: `${stats.totalSchools} scholen aangesloten op het platform`,
        type: 'info' as const
      });
    }
    activities.push({
      time: 'Vandaag',
      message: `Vragenbank: ${stats.totalQuestions} vragen geladen`,
      type: 'success' as const
    });
    return activities;
  }, [stats, pipelineResult, passCount, failCount]);
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" data-id="element-399">
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-400">
        <motion.div variants={itemVariants} data-id="element-401">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-5 h-5" data-id="element-403" />} color="purple" data-id="element-402" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-404">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-5 h-5" data-id="element-406" />} color="blue" data-id="element-405" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-407">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-5 h-5" data-id="element-409" />} color="emerald" data-id="element-408" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-410">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-5 h-5" data-id="element-412" />} color="rose" data-id="element-411" />
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-413">
        <motion.div variants={itemVariants} data-id="element-414">
          <StatCard label="Vragenbank" value={stats.totalQuestions} subtitle="vragen totaal" icon={<BookOpen className="w-5 h-5" data-id="element-416" />} color="blue" data-id="element-415" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-417">
          {avgScore !== null ? <RingChart score={avgScore} label="Kwaliteitsscore" subtitle={`steekproef ${PIPELINE_SAMPLE_SIZE} vragen`} data-id="element-418" /> : <StatCard label="Kwaliteitsscore" value="laden..." subtitle="steekproef wordt geanalyseerd" icon={<ShieldCheck className="w-5 h-5" data-id="element-420" />} color="amber" data-id="element-419" />}
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-421">
          <StatCard label="Actief Vandaag" value={stats.activeToday} subtitle="leerlingen" icon={<Activity className="w-5 h-5" data-id="element-423" />} color="purple" data-id="element-422" />
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-id="element-424">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-425">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-426">
            <BookOpen className="w-4 h-4" data-id="element-427" />
            Content Dekking
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-428">
            <span className="text-2xl font-bold text-gray-900" data-id="element-429">
              {contentStats.coveragePct}%
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-430">
              {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}{' '}
              families
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-431">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${contentStats.coveragePct}%`
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className={`h-full rounded-full ${contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-432" />
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-433">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-434">
            <Layers className="w-4 h-4" data-id="element-435" />
            Vraagtypen
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-436">
            <span className="text-2xl font-bold text-gray-900" data-id="element-437">
              {Object.keys(contentStats.typeDistribution).length}
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-438">actieve typen</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-439">
            {Object.entries(contentStats.typeDistribution).sort((a, b) => b[1] - a[1]).map(([type, count], i) => {
            const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
            return <div key={type} className={`h-full ${colors[i % colors.length]}`} style={{
              width: `${count / contentStats.totalQuestions * 100}%`
            }} title={`${type}: ${count}`} data-id="element-440" />;
          })}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-441">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-442">
            <ImageIcon className="w-4 h-4" data-id="element-443" />
            Visuele Componenten
          </h3>
          <div className="flex items-end justify-between mb-2" data-id="element-444">
            <span className="text-2xl font-bold text-gray-900" data-id="element-445">
              {contentStats.visualCoveragePct}%
            </span>
            <span className="text-sm text-gray-500 mb-1" data-id="element-446">
              {contentStats.usedVisualsCount} / {contentStats.registeredVisuals}{' '}
              in gebruik
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-447">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${contentStats.visualCoveragePct}%`
          }} transition={{
            duration: 1,
            delay: 0.4
          }} className={`h-full rounded-full ${contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-448" />
          </div>
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-449">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-6" data-id="element-450">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-451">
            <Clock className="w-4 h-4 text-gray-400" data-id="element-452" />
            Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-453">
            {RECENT_ACTIVITY.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-454">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-455">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-456" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-457" /> : <Activity className="w-4 h-4" data-id="element-458" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-459">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-460">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-id="element-461">{item.time}</p>
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-6" data-id="element-462">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-463">
            <Server className="w-4 h-4 text-gray-400" data-id="element-464" />
            Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-465">
            <div className="flex items-center justify-between" data-id="element-466">
              <span className="text-sm text-gray-600" data-id="element-467">Uptime</span>
              <div className="flex items-center gap-2" data-id="element-468">
                <span className="text-sm font-semibold text-gray-400" data-id="element-469">
                  N/A (Client-side app)
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-470">
              <span className="text-sm text-gray-600" data-id="element-471">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-472">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-473">
              <span className="text-sm text-gray-600" data-id="element-474">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-475">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-476">
              <div className="flex items-center justify-between mb-1.5" data-id="element-477">
                <span className="text-sm text-gray-600" data-id="element-478">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-479">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-480">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-481" />
              </div>
            </div>
            <div data-id="element-482">
              <div className="flex items-center justify-between mb-1.5" data-id="element-483">
                <span className="text-sm text-gray-600" data-id="element-484">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-485">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-486">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-487" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-488">
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-489">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-490">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-491">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-492">
                <div className="flex justify-between text-xs mb-1" data-id="element-493">
                  <span className="text-gray-600" data-id="element-494">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-495">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-496">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-497" />
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-498">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-499">
            <TrendingUp className="w-3.5 h-3.5 inline mr-1" data-id="element-500" />
            Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-501">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-502">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-503">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-504">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-505">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-506">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-507">Geen leerlingen gevonden</p>}
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-200 p-5" data-id="element-508">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center justify-between" data-id="element-509">
            <span data-id="element-510">
              <Zap className="w-3.5 h-3.5 inline mr-1" data-id="element-511" />
              Validatiestatus
            </span>
            <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-512">
              <RefreshCw className={`w-3.5 h-3.5 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-513" />
            </button>
          </h3>
          {pipelineLoading ? <div className="space-y-2.5" data-id="element-514">
              {['Geslaagd', 'Waarschuwingen', 'Fouten'].map(label => <div key={label} className="flex items-center justify-between" data-id="element-515">
                  <span className="text-sm text-gray-600" data-id="element-516">{label}</span>
                  <div className="h-4 w-8 bg-gray-100 rounded animate-pulse" data-id="element-517" />
                </div>)}
            </div> : pipelineResult ? <div className="space-y-2.5" data-id="element-518">
              <div className="flex items-center justify-between" data-id="element-519">
                <span className="text-sm text-gray-600" data-id="element-520">Geslaagd</span>
                <span className="text-sm font-bold text-emerald-600" data-id="element-521">
                  {passCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-522">
                <span className="text-sm text-gray-600" data-id="element-523">Waarschuwingen</span>
                <span className="text-sm font-bold text-amber-600" data-id="element-524">
                  {warnCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-525">
                <span className="text-sm text-gray-600" data-id="element-526">Fouten</span>
                <span className="text-sm font-bold text-red-600" data-id="element-527">
                  {failCount}
                </span>
              </div>
              <div className="pt-1 border-t border-gray-100" data-id="element-528">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-529">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-530" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-531" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-532" />
                </div>
                <p className="text-xs text-gray-400 mt-1.5" data-id="element-533">
                  Steekproef: {PIPELINE_SAMPLE_SIZE} vragen
                </p>
              </div>
            </div> : <div className="space-y-2.5" data-id="element-534">
              {['Geslaagd', 'Waarschuwingen', 'Fouten'].map(label => <div key={label} className="flex items-center justify-between" data-id="element-535">
                  <span className="text-sm text-gray-600" data-id="element-536">{label}</span>
                  <span className="text-sm font-bold text-gray-300" data-id="element-537">—</span>
                </div>)}
            </div>}
        </motion.div>
      </motion.div>
    </PageLayout>;
}